import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { I18nContext } from 'src/stores/I18n';
import {
  getIsFollowingDayAccess,
  getIsMoreThanThreeTimes,
  prevIsFollowingDayAccessCache,
  productPageViewCountCache,
  resetAppBannerDisplayConditionCache
} from 'src/utils/appBanner';
import { mixpanel } from 'src/utils/mixpanel';

const PRODUCT_PAGE_PATH_PATTERN = /^\/products\/\d+/;
const APP_BANNER_MIXPANEL_EVENT_NAME = 'AppDownloadBannerShown';
const DISPLAY_COUNT_KEY = 'displayCount';
const BANNER_CLOSED_COUNT_KEY = 'bannerClosedCount';
const DOWNLOAD_BUTTON_CLICKED_COUNT_KEY = 'downloadButtonClicked';

const TRUE = 1;
const FALSE = 0;

type MixpanelEventType = 'displayCount' | 'bannerClosedCount' | 'downloadButtonClicked';

export const useAppBanner = () => {
  const [isFollowingDayAccess, setIsFollowingDayAccess] = useState(false);
  const [productPageViewMoreThanThree, setProductPageViewMoreThanThree] = useState(false);
  const [prevIsFollowingDayAccess, setPrevIsFollowingDayAccess] = useState(!!prevIsFollowingDayAccessCache.get());
  const [prevProductPageViewMoreThanThree, setPrevProductPageViewMoreThanThree] = useState(
    productPageViewCountCache.get() >= 3
  );
  const localeResources = useContext(I18nContext);
  const { asPath: path } = useRouter();

  useEffect(() => {
    const isProductPage = PRODUCT_PAGE_PATH_PATTERN.test(path);
    if (isProductPage) {
      setProductPageViewMoreThanThree(getIsMoreThanThreeTimes());
    }
    setIsFollowingDayAccess(getIsFollowingDayAccess());
    isFollowingDayAccess && prevIsFollowingDayAccessCache.set(TRUE);
  }, [isFollowingDayAccess, path]);

  const sendMixpanelEvent = (type: MixpanelEventType) => {
    mixpanel.track(APP_BANNER_MIXPANEL_EVENT_NAME, { type });
  };

  useEffect(() => {
    const isAppBannerAppearedConditionToggledToTrue =
      (!prevProductPageViewMoreThanThree && productPageViewMoreThanThree) ||
      (!prevIsFollowingDayAccess && isFollowingDayAccess);
    const isAppBannerAppearedConditionNotDuplicated =
      (!productPageViewMoreThanThree && isFollowingDayAccess) ||
      (!isFollowingDayAccess && productPageViewMoreThanThree);

    if (isAppBannerAppearedConditionToggledToTrue && isAppBannerAppearedConditionNotDuplicated) {
      sendMixpanelEvent(DISPLAY_COUNT_KEY);
      setPrevProductPageViewMoreThanThree(productPageViewMoreThanThree);
      setPrevIsFollowingDayAccess(true);
    }
  }, [isFollowingDayAccess, prevIsFollowingDayAccess, prevProductPageViewMoreThanThree, productPageViewMoreThanThree]);

  const handleDownloadButtonClick = () => {
    sendMixpanelEvent(DOWNLOAD_BUTTON_CLICKED_COUNT_KEY);
  };

  const handleClose = () => {
    setIsFollowingDayAccess(false);
    setProductPageViewMoreThanThree(false);
    setPrevProductPageViewMoreThanThree(false);
    setPrevIsFollowingDayAccess(false);
    resetAppBannerDisplayConditionCache();
    prevIsFollowingDayAccessCache.set(FALSE);
    sendMixpanelEvent(BANNER_CLOSED_COUNT_KEY);
  };

  return {
    isFollowingDayAccess,
    productPageViewMoreThanThree,
    localeResources,
    handleClose,
    handleDownloadButtonClick
  };
};
